import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  MotifFormField,
  MotifSearch,
  MotifLabel,
  MotifToggleSwitch,
  MotifToast,
  MotifButton,
  MotifTable,
} from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleFirstDataRendered,
  handleGridReady,
  tableHeaderClass,
  handleItemsPerPageChange,
} from '../../common/AgGridEventHandlers';
import ReitsCellRenderer from './customRenderer/ReitsCellRenderer';
import CustomPagination from '../../common/MotifPagination';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';

const ReitList = ({
  clientId,
  client,
  reits,
  getCurrentPeriodFunction,
  handleSearchChange,
  handleClearSearchText,
  reitSearchText,
  handleShowInactive,
  showInactive,
  canInactivateReits,
  handleToggleActiveFlag,
  handleDownloadIdList,
  handleDownloadTbTemplates,
  showSuccessMessage,
  handleDismissSuccessMessage,
  successMessageText,
  chartExistsForClient,
  handleGridChanges,
  theme,
  hasIncompleteSecuritySnapshotsList,
}) => {
  let allOrActive = 'Active';
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  const getRowId = useCallback((params) => params.data.reitid, []);

  if (showInactive) {
    allOrActive = 'All';
  }
  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const columnDefs = useMemo(() => {
    const cols = [
      {
        field: 'reitName',
        headerName: 'REIT NAME',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 300,
        cellRenderer: ReitsCellRenderer,
        cellRendererParams: { hasIncompleteSecuritySnapshotsList },
        headerComponentParams: {
          template: CustomHeaderTemplate('ReitName'),
        },

        filterValueGetter: (params) => {
          return params.data && params.data.reitName;
        },
      },
      {
        field: 'reportPeriodDescription',
        headerName: 'CURRENT PERIOD',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 250,
        valueGetter: (params) => {
          if (params.data.periods && params.data.periods.length > 0) {
            return (
              params.data && params.data.periods[0].reportPeriodDescription
            );
          } else {
            return 'Not Found';
          }
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('CurrentPeriod'),
        },
        filterValueGetter: (params) => {
          if (params.data.periods && params.data.periods.length > 0) {
            return (
              params.data && params.data.periods[0].reportPeriodDescription
            );
          }
        },
      },

      {
        field: 'reportPeriodStatusDescription',
        headerName: 'STATUS',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 200,
        cellRenderer: ReitsCellRenderer,
        valueGetter: (params) => {
          if (params.data.periods && params.data.periods.length > 0) {
            return (
              params.data &&
              params.data.periods[0].reportPeriodStatusDescription
            );
          }
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('Status'),
        },
        filterValueGetter: (params) => {
          if (params.data.periods && params.data.periods.length > 0) {
            return (
              params.data &&
              params.data.periods[0].reportPeriodStatusDescription
            );
          } else {
            return 'Not Found';
          }
        },
      },
      {
        field: 'reitTrackingID',
        headerName: 'TRACKING ID',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 250,
        headerComponentParams: {
          template: CustomHeaderTemplate('TrackingId'),
        },
      },
    ];

    if (showInactive) {
      cols.push({
        field: 'isActive',
        headerName: 'Active',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 150,
        cellRenderer: ReitsCellRenderer,
        cellRendererParams: { handleToggleActiveFlag },
        hide: !showInactive,
        headerComponentParams: {
          template: CustomHeaderTemplate('isActive'),
        },
      });
    }

    return cols;
  }, [
    hasIncompleteSecuritySnapshotsList,
    showInactive,
    handleToggleActiveFlag,
  ]);

  //Handle deselect all event
  const handleDeselectAll = useCallback((val) => {
    // set current page and total pages to 0
    setCurrentPage(0);
    setTotalPages(0);
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
    if (rowData.length > 0 && gridRef.current.api !== undefined) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }

      if (gridRef.current.columnApi) {
        gridRef.current.columnApi.setColumnVisible('isActive', showInactive);
      }

      gridRef.current.api.refreshCells();
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(reits);
    if (gridRef && gridRef.current && gridRef.current.api) {
      gridRef.current.api.applyTransaction({});
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [reits]);

  useEffect(() => {
    localStorage.removeItem('REITSuiteReitListColumnState');

    // set the current page to 1 and reset the grid to first page
    if (gridRef.current?.api) {
      setCurrentPage(1);
      gridRef.current.api.paginationGoToPage(0);
    }
  }, [showInactive]);
  const clientIsAudit = client && client.isAudit;

  const GetCurrentQuarter = useCallback(() => {
    let currentQuarter = 'Q';
    let today = new Date();

    switch (today.getMonth()) {
      case 0:
      case 1:
      case 2:
        currentQuarter += '1';
        break;

      case 3:
      case 4:
      case 5:
        currentQuarter += '2';
        break;

      case 6:
      case 7:
      case 8:
        currentQuarter += '3';
        break;

      case 9:
      case 10:
      case 11:
        currentQuarter += '4';
        break;

      default:
        currentQuarter += '1';
        break;
    }

    currentQuarter += ' ' + (today.getYear() + 1900);

    return currentQuarter;
  }, []);

  const downloadIdList = useCallback(
    () =>
      handleDownloadIdList(
        client.id,
        showInactive,
        `ID List for ${client.name}.xlsx`
      ),
    [client.id, client.name, showInactive, handleDownloadIdList]
  );

  const downloadTbTemplates = useCallback(
    () => handleDownloadTbTemplates(client.id, showInactive),
    [client.id, showInactive, handleDownloadTbTemplates]
  );

  const rowHeight = 79;
  const headerHeight = 55;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default if compact={false}

  return (
    <React.Fragment>
      {showSuccessMessage && (
        <MotifToast
          className="mt-2 mb-2"
          variant="success"
          onClose={handleDismissSuccessMessage}
        >
          {successMessageText}
        </MotifToast>
      )}
      <div className="row align-items-center">
        <div className="col-md-4">
          <h2 data-testid="pghdrReit">REITs</h2>
        </div>
        <div className="col-md-5 text-right">
          {canInactivateReits && (
            <React.Fragment>
              <MotifLabel
                htmlFor="switchInactive"
                className="text-right overflow-visible-Toggle"
                style={{
                  display: 'inline-block',
                  marginRight: '1rem',
                  width: 'auto',
                  marginTop: '30px',
                }}
              >
                Display Inactive REITs:
              </MotifLabel>
              <MotifFormField
                style={{
                  display: 'inline-block',
                  width: 'auto',
                  marginLeft: '1rem',
                }}
              >
                <MotifToggleSwitch
                  aria-label="Display Inactive REITs toogle"
                  name="switchInactive"
                  id="switchInactive"
                  data-testid="tglDisplayInactiveReits"
                  onChange={handleShowInactive}
                  checked={!!showInactive}
                />
              </MotifFormField>
            </React.Fragment>
          )}
        </div>
        <div className="col-md-3">
          <MotifFormField>
            <MotifSearch
              type="text"
              items={[]}
              name="reitFilter"
              onChange={handleSearchChange}
              onClear={handleClearSearchText}
              value={reitSearchText}
              title="Type in a REIT name"
              searchButtonProps={{ tabIndex: '-1' }}
              aria-label="Search for REITs"
              placeholder="Search for REITs"
              data-testid="txtSearchReit"
            />
          </MotifFormField>
        </div>
      </div>
      {!clientIsAudit && (
        <div style={{ width: '100%', textAlign: 'left', marginTop: '15px' }}>
          <span style={{ display: 'inline-block', float: 'left' }}>
            <MotifButton
              variant="secondary"
              type="button"
              data-testid="btnDownloadActiveTrackingId"
              onClick={downloadIdList}
              disabled={allOrActive === 'Active' && reits.length === 0}
            >
              Download {allOrActive} Tracking IDs
            </MotifButton>
          </span>
          <span style={{ display: 'inline-block' }}>
            <MotifButton
              variant="secondary"
              className="ml-2"
              type="button"
              data-testid="btnDownloadActiveTBTemplates"
              onClick={downloadTbTemplates}
              disabled={!chartExistsForClient}
            >
              Download {allOrActive} TB Templates
            </MotifButton>
          </span>
        </div>
      )}

      <div>
        {rowData.length > 0 && (
          <div className="mt-2">
            <div className="row">
              <div className="col">
                <div
                  className="ag-theme-quartz motif-table table-bd-full-height-width"
                  data-testid="grdReitDashboard"
                >
                  <MotifTable
                    zebra={true}
                    compact={true}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowId}
                    accentedSort={true}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    onGridReady={(event) =>
                      handleGridReady(
                        event,
                        gridRef,
                        setCurrentPage,
                        setItemsPerPage,
                        setTotalPages,
                        'REITSuiteReitListColumnState',
                        handleDeselectAll
                      )
                    }
                    onFirstDataRendered={handleFirstDataRendered(gridRef)}
                    ref={gridRef}
                    pagination={true}
                    suppressPaginationPanel={true}
                    paginationPageSize={100}
                    groupDisplayType={'custom'}
                    groupSelectsChildren={true}
                    groupDefaultExpanded={0}
                    onFilterChanged={handleFilterChanged(
                      gridRef,
                      currentPage,
                      setCurrentPage,
                      setTotalPages
                    )}
                    data-testid="divClientListGrid"
                    deltaRowDataMode={true}
                    immutableData={true}
                  />
                  <div className="mt-3">
                    <span className="right mb-3">
                      <CustomPagination
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                        totalPages={totalPages}
                        handleItemsPerPageChange={handleItemsPerPageChange}
                        gridRef={gridRef}
                        setCurrentPage={setCurrentPage}
                        setItemsPerPage={setItemsPerPage}
                        setTotalPages={setTotalPages}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {(reits && reits.length === 0 && reitSearchText === '' && (
          <div className="mt-3">No REIT found for this client.</div>
        )) ||
          (reits && reits.length === 0 && reitSearchText !== '' && (
            <div className="mt-3">
              <h5>No REITs match your search text '{reitSearchText}'</h5>
            </div>
          )) ||
          null}
      </div>
    </React.Fragment>
  );
};

ReitList.propTypes = {
  clientId: PropTypes.number.isRequired,
  client: PropTypes.object.isRequired,
  reits: PropTypes.array.isRequired,
  getCurrentPeriodFunction: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  handleClearSearchText: PropTypes.func.isRequired,
  reitSearchText: PropTypes.string,
  handleShowInactive: PropTypes.func.isRequired,
  showInactive: PropTypes.bool,
  canInactivateReits: PropTypes.bool,
  handleToggleActiveFlag: PropTypes.func.isRequired,
  handleDownloadIdList: PropTypes.func.isRequired,
  handleDownloadTbTemplates: PropTypes.func.isRequired,
  handleDismissSuccessMessage: PropTypes.func,
  showSuccessMessage: PropTypes.bool,
  successMessageText: PropTypes.string,
  incompleteSecuritiesSnapshots: PropTypes.array,
};

export default ReitList;
